import { ComponentPropsWithoutRef, ReactElement } from "react"
import { Icon } from "../Icon"
import { Button } from "../Button"
import { Badge } from "../Badge"
import classNames from "classnames"
import { FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import {
  COLOUR_SCHEMES,
  BASE_CLASSES,
  COLOUR_SCHEMES_HOVER,
  BASE_CLASSES_DISABLED,
  COLOUR_SCHEMES_DISABLED,
  BASE_CLASSES_ACTIVE,
  BASE_CLASSES_FOCUS,
  BASE_CLASSES_ENABLED,
} from "../Button/constants"

function MobileButton({
  hasActiveFilters,
  disabled,
  children = "Filter",
  activeFiltersBadge,
  id,
  className,
  ...rest
}: Omit<TableFilterButtonProps, "activeFilters"> & {
  hasActiveFilters: boolean
  activeFiltersBadge: ReactElement | undefined
}) {
  // This button is a one off implementation specific to the Table
  const _className = classNames(
    className,
    BASE_CLASSES,
    FOCUS_STATE_BASE_CLASSES,
    "h-11 rounded-lg p2 text-base leading-6", // Same as BASE_CLASSES_MD.replace('px-6', 'px-3')
    "aria-pressed:bg-primary-lighten-100",
    BASE_CLASSES_DISABLED,
    BASE_CLASSES_ENABLED,
    BASE_CLASSES_ACTIVE,
    BASE_CLASSES_FOCUS,
    hasActiveFilters
      ? [
          COLOUR_SCHEMES["secondary"],
          !disabled && COLOUR_SCHEMES_HOVER["secondary"],
          disabled && COLOUR_SCHEMES_DISABLED["secondary"],
        ]
      : [
          "aspect-square",
          COLOUR_SCHEMES["outline"],
          !disabled && COLOUR_SCHEMES_HOVER["outline"],
          disabled && COLOUR_SCHEMES_DISABLED["outline"],
        ],
  )

  return (
    <button
      aria-label={typeof children === "string" ? children : undefined}
      aria-pressed={hasActiveFilters}
      className={_className}
      disabled={disabled}
      id={id ? `mobile-${id}` : undefined}
      {...rest}
    >
      <div className="grid grid-flow-col place-items-center gap-2 p-2">
        <Icon name="Filter" size="sm" />
        {activeFiltersBadge}
      </div>
    </button>
  )
}

type TableFilterButtonProps = ComponentPropsWithoutRef<"button"> & {
  activeFilters: number
}

export const TableFilterButton = ({
  children = "Filter",
  id,
  activeFilters,
  disabled,
  ...rest
}: TableFilterButtonProps) => {
  // For now we won't pass in the ref because the ui library does not have access to Tailwind's theme object.
  // We need the theme object in order to use JS to determine which of these to render and therefore pass the ref to

  const hasActiveFilters = activeFilters > 0
  const activeFiltersBadge = hasActiveFilters ? (
    <Badge
      isOutline
      variation="success"
      className={activeFilters <= 9 ? "aspect-square" : undefined}
      size="xs"
    >
      {activeFilters}
    </Badge>
  ) : undefined

  return (
    <>
      <MobileButton
        className="row-start-1 block md:hidden"
        hasActiveFilters={hasActiveFilters}
        activeFiltersBadge={activeFiltersBadge}
        id={id}
        disabled={disabled}
        {...rest}
      >
        {children}
      </MobileButton>

      {/* Tablet and desktop button */}
      <Button
        aria-pressed={hasActiveFilters}
        aria-label={typeof children === "string" ? children : undefined}
        className="row-start-1 hidden aria-pressed:bg-primary-lighten-100 md:block"
        disabled={disabled}
        endAdornment={activeFiltersBadge}
        id={id ? `desktop-${id}` : undefined}
        size="sm"
        startAdornment={<Icon name="Filter" />}
        variation={hasActiveFilters ? "secondary" : "outline"}
        {...rest}
      >
        {children}
      </Button>
    </>
  )
}
